// Configuración · maestros simples (docs/108 B10), sobre el Mantenedor genérico. Lo que el backend no
// permite editar (categorías, marcas, vendedores, zonas) se crea y se ve; lo sensible pide motivo.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useComunas } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { fecha, fechaHora, rut, tasa } from '../comun/formato';
import { decimalApi, Mantenedor, siTexto, type DefMantenedor } from '../comun/Mantenedor';
import { useSesion } from '../sesion/SesionContexto';

type Fila = Record<string, unknown> & { id?: string };
const t = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const si = (v: unknown) => (v ? 'sí' : 'no');

// ================================================================ categorías y marcas

export function Categorias() {
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Categorías',
          claveConsulta: ['config', 'categorias'],
          listar: () => api.get<Fila[]>('/catalogo/categorias'),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '9rem',
            },
            { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
            { id: 'nv', titulo: 'Nivel', celda: (f) => t(f['nivel']), ancho: '5rem' },
            { id: 'a', titulo: 'Activa', celda: (f) => si(f['activa']), ancho: '5rem' },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_-]{1,20}$/,
              ayuda: 'MAYÚSCULAS, números, - y _',
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
          ],
          crear: (v) => ({
            ruta: '/catalogo/categorias',
            cuerpo: { codigo: v['codigo'], nombre: v['nombre'] },
          }),
          permisoCrear: 'catalogo.clasificacion.gestionar',
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

export function Marcas() {
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Marcas',
          claveConsulta: ['config', 'marcas'],
          listar: () => api.get<Fila[]>('/catalogo/marcas'),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '9rem',
            },
            { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
            { id: 'a', titulo: 'Activa', celda: (f) => si(f['activa']), ancho: '5rem' },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_-]{1,20}$/,
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
          ],
          crear: (v) => ({
            ruta: '/catalogo/marcas',
            cuerpo: { codigo: v['codigo'], nombre: v['nombre'] },
          }),
          valoresDe: (f) => ({ codigo: t(f['codigo']), nombre: t(f['nombre']) }),
          editar: (f, v) => ({
            ruta: `/catalogo/marcas/${t(f.id)}`,
            cuerpo: { nombre: v['nombre'], version: Number(f['version']) },
            metodo: 'PATCH',
          }),
          acciones: [
            {
              texto: 'Activar / desactivar',
              permiso: 'catalogo.clasificacion.gestionar',
              pedido: (f) => ({
                ruta: `/catalogo/marcas/${t(f.id)}`,
                cuerpo: { activa: !f['activa'], version: Number(f['version']) },
                metodo: 'PATCH',
              }),
            },
          ],
          permisoCrear: 'catalogo.clasificacion.gestionar',
          permisoEditar: 'catalogo.clasificacion.gestionar',
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

// ================================================================ vendedores

export function Vendedores() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Vendedores',
          claveConsulta: ['config', 'vendedores', empresaId],
          listar: async () =>
            (await api.get<Fila[]>('/vendedores')).filter((v) => v['empresa_id'] === empresaId),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '7rem',
            },
            { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
            {
              id: 'tp',
              titulo: 'Tipo',
              celda: (f) => t(f['tipo']).replace('_', ' '),
              ancho: '8rem',
            },
            {
              id: 'u',
              titulo: 'Usuario',
              celda: (f) => (f['usuario_id'] ? 'vinculado' : ''),
              ancho: '7rem',
            },
            { id: 'a', titulo: 'Activo', celda: (f) => si(f['activo']), ancho: '5rem' },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_-]{1,12}$/,
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
            {
              id: 'tipo',
              etiqueta: 'Tipo',
              tipo: 'seleccion',
              opciones: ['interno', 'ruta', 'call_center', 'virtual', 'externo'].map((x) => ({
                valor: x,
                texto: x.replace('_', ' '),
              })),
            },
          ],
          crear: (v) => ({
            ruta: '/vendedores',
            cuerpo: {
              empresaId,
              codigo: v['codigo'],
              nombre: v['nombre'],
              ...siTexto('tipo', v['tipo']),
            },
          }),
          permisoCrear: 'entidades.gestionar',
          nota: (
            <p className="apagado">
              El backend no edita vendedores: se crean (el código no se reutiliza).
            </p>
          ),
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

// ================================================================ zonas logísticas

export function Zonas() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const comunas = useComunas();
  const [editando, setEditando] = useState<Fila | null>(null);
  return (
    <>
      <Mantenedor
        def={
          {
            etiqueta: 'Zonas',
            claveConsulta: ['config', 'zonas', empresaId],
            listar: () => api.get<Fila[]>('/ventas/zonas', { query: { empresaId } }),
            clave: (f) => t(f.id),
            textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
            columnas: [
              {
                id: 'c',
                titulo: 'Código',
                celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
                ancho: '8rem',
              },
              { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
              {
                id: 'co',
                titulo: 'Comunas',
                celda: (f) =>
                  ((f['comunas'] as Array<{ nombre: string }> | undefined) ?? [])
                    .map((c) => c.nombre)
                    .join(', '),
              },
            ],
            campos: [
              {
                id: 'codigo',
                etiqueta: 'Código',
                requerido: true,
                soloCrear: true,
                patron: /^[A-Z0-9_-]{1,20}$/,
              },
              { id: 'nombre', etiqueta: 'Nombre', requerido: true },
            ],
            crear: (v) => ({
              ruta: '/ventas/zonas',
              cuerpo: { empresaId, codigo: v['codigo'], nombre: v['nombre'] },
            }),
            permisoCrear: 'ventas.zona.gestionar',
            acciones: [
              {
                texto: 'Comunas',
                permiso: 'ventas.zona.gestionar',
                abrir: (f) => setEditando(f),
              },
            ],
          } satisfies DefMantenedor<Fila>
        }
      />
      {editando && (
        <ComunasDeZona
          zona={editando}
          comunas={comunas.data ?? []}
          alCerrar={() => setEditando(null)}
        />
      )}
    </>
  );
}

function ComunasDeZona({
  zona,
  comunas,
  alCerrar,
}: {
  zona: Fila;
  comunas: Array<{ codigo: string; nombre: string }>;
  alCerrar: () => void;
}) {
  const inicial = new Set(
    ((zona['comunas'] as Array<{ codigo: string }> | undefined) ?? []).map((c) => c.codigo),
  );
  const [sel, setSel] = useState<Set<string>>(inicial);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const lista = comunas.filter(
    (c) => !filtro || c.nombre.toLowerCase().includes(filtro.toLowerCase()),
  );
  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await api.put(
        `/ventas/zonas/${t(zona.id)}/comunas`,
        { comunas: [...sel] },
        { idempotencia: `zona-${crypto.randomUUID()}` },
      );
      alCerrar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div className="panel-revision" role="form" aria-label={`Comunas de ${t(zona['nombre'])}`}>
      <div className="barra-filtros">
        <b>Comunas de {t(zona['nombre'])}</b>
        <label className="campo">
          <span className="etiqueta">Buscar comuna</span>
          <input
            aria-label="Buscar comuna"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </label>
        <span>{sel.size} elegidas</span>
        <span className="espaciador" />
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!sel.size || guardando}
          onClick={() => void guardar()}
        >
          Guardar comunas
        </button>
      </div>
      {error ? <PanelError error={error} /> : null}
      <div className="lista-comunas" style={{ columns: 4, maxHeight: '18rem', overflow: 'auto' }}>
        {lista.map((c) => (
          <label key={c.codigo} style={{ display: 'block' }}>
            <input
              type="checkbox"
              checked={sel.has(c.codigo)}
              onChange={() =>
                setSel((s) => {
                  const n = new Set(s);
                  if (n.has(c.codigo)) n.delete(c.codigo);
                  else n.add(c.codigo);
                  return n;
                })
              }
            />{' '}
            {c.nombre}
          </label>
        ))}
      </div>
    </div>
  );
}

// ================================================================ impuestos y tasas

export function Impuestos() {
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Impuestos',
          claveConsulta: ['config', 'impuestos'],
          listar: () => api.get<Fila[]>('/tributario/impuestos'),
          clave: (f) => t(f['codigo']),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '8rem',
            },
            { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
            { id: 'tp', titulo: 'Tipo', celda: (f) => t(f['tipo']), ancho: '7rem' },
            { id: 'v', titulo: 'Venta', celda: (f) => si(f['aplica_en_venta']), ancho: '5rem' },
            { id: 'cp', titulo: 'Compra', celda: (f) => si(f['aplica_en_compra']), ancho: '5rem' },
            { id: 'sii', titulo: 'Código SII', celda: (f) => t(f['codigo_sii']), ancho: '7rem' },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_]{2,20}$/,
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
            {
              id: 'tipo',
              etiqueta: 'Tipo',
              tipo: 'seleccion',
              requerido: true,
              opciones: ['iva', 'adicional', 'retencion', 'especifico', 'exento'].map((x) => ({
                valor: x,
                texto: x,
              })),
            },
            { id: 'codigoSii', etiqueta: 'Código SII', ayuda: 'Ej.: 27 (ILA 10 %), 24 (licores)' },
            { id: 'aplicaEnCompra', etiqueta: 'Aplica en compra', tipo: 'booleano' },
          ],
          crear: (v, motivo) => ({
            ruta: '/tributario/impuestos',
            cuerpo: {
              codigo: v['codigo'],
              nombre: v['nombre'],
              tipo: v['tipo'],
              ...siTexto('codigoSii', v['codigoSii']),
              aplicaEnCompra: v['aplicaEnCompra'] !== 'false',
              motivo,
            },
          }),
          conMotivo: true,
          permisoCrear: 'tributario.impuestos.gestionar',
          detalle: (f) => <TasasDeImpuesto codigo={t(f['codigo'])} />,
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

function TasasDeImpuesto({ codigo }: { codigo: string }) {
  return (
    <>
      <h3 className="titulo-seccion">Tasas de {codigo}</h3>
      <Mantenedor
        def={
          {
            etiqueta: `Tasas de ${codigo}`,
            claveConsulta: ['config', 'tasas', codigo],
            listar: () => api.get<Fila[]>(`/tributario/impuestos/${codigo}/tasas`),
            clave: (f) => t(f.id),
            columnas: [
              { id: 'p', titulo: 'Tasa', celda: (f) => tasa(t(f['porcentaje'])), ancho: '7rem' },
              {
                id: 'd',
                titulo: 'Desde',
                celda: (f) => fecha(t(f['vigencia_desde'])),
                ancho: '8rem',
              },
              {
                id: 'h',
                titulo: 'Hasta',
                celda: (f) => (f['vigencia_hasta'] ? fecha(t(f['vigencia_hasta'])) : 'vigente'),
                ancho: '8rem',
              },
              { id: 'f', titulo: 'Fuente', celda: (f) => t(f['fuente']) },
            ],
            campos: [
              {
                id: 'porCiento',
                etiqueta: 'Tasa (%)',
                tipo: 'decimal',
                requerido: true,
                ayuda: 'Ej.: 19 o 20,5',
              },
              { id: 'vigenciaDesde', etiqueta: 'Vigente desde', tipo: 'fecha', requerido: true },
              { id: 'fuente', etiqueta: 'Fuente (ley, circular)' },
            ],
            crear: (v, motivo) => ({
              ruta: '/tributario/tasas',
              cuerpo: {
                impuestoCodigo: codigo,
                porCiento: decimalApi(v['porCiento']),
                vigenciaDesde: v['vigenciaDesde'],
                ...siTexto('fuente', v['fuente']),
                motivo,
              },
            }),
            conMotivo: true,
            permisoCrear: 'tributario.impuestos.gestionar',
          } satisfies DefMantenedor<Fila>
        }
      />
    </>
  );
}

// ================================================================ bodegas (ubicaciones del inventario)

const TIPOS_UBIC = ['BODEGA', 'CAMION', 'MESON', 'MERMA', 'CUARENTENA'];

export function Bodegas() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Bodegas y ubicaciones',
          claveConsulta: ['emp', empresaId, 'ubicaciones'],
          listar: () => api.get<Fila[]>('/inventario/ubicaciones', { query: { empresaId } }),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['nombre'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '7rem',
            },
            { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
            {
              id: 'tp',
              titulo: 'Tipo',
              celda: (f) => t(f['tipo']) || <span className="tono-malo">sin clasificar</span>,
              ancho: '9rem',
            },
            { id: 'o', titulo: 'Origen', celda: (f) => t(f['origen']), ancho: '7rem' },
            {
              id: 'l',
              titulo: 'Clasificación del legado',
              celda: (f) => t(f['clasificacion_legado']),
              ancho: '12rem',
            },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_-]{1,20}$/,
            },
            { id: 'nombre', etiqueta: 'Nombre', requerido: true },
          ],
          crear: (v, motivo) => ({
            ruta: '/inventario/ubicaciones',
            cuerpo: { empresaId, codigo: v['codigo'], nombre: v['nombre'], motivo },
          }),
          conMotivo: true,
          permisoCrear: 'inventario.ubicaciones.gestionar',
          acciones: TIPOS_UBIC.map((tipo) => ({
            texto: tipo.charAt(0) + tipo.slice(1).toLowerCase(),
            conMotivo: true,
            permiso: 'inventario.ubicaciones.gestionar',
            visible: (f: Fila) => f['tipo'] !== tipo,
            pedido: (f: Fila, motivo: string) => ({
              ruta: `/inventario/ubicaciones/${t(f.id)}/clasificar`,
              cuerpo: { tipo, motivo },
            }),
          })),
          nota: (
            <p className="apagado">
              El tipo decide qué se puede hacer en la ubicación: vender y despachar desde BODEGA o
              MESÓN, cargar a CAMIÓN, mandar a MERMA o CUARENTENA.
            </p>
          ),
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

// ================================================================ empresas

export function Empresas() {
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Empresas',
          claveConsulta: ['sesion', 'empresas'],
          listar: () => api.get<Fila[]>('/empresas'),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['codigo'])} ${t(f['razon_social'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Código',
              celda: (f) => <span className="mono">{t(f['codigo'])}</span>,
              ancho: '6rem',
            },
            { id: 'r', titulo: 'RUT', celda: (f) => rut(t(f['rut']), t(f['dv'])), ancho: '9rem' },
            { id: 'n', titulo: 'Razón social', celda: (f) => t(f['razon_social']) },
            { id: 'g', titulo: 'Giro', celda: (f) => t(f['giro']) },
            { id: 'co', titulo: 'Comuna', celda: (f) => t(f['comuna']), ancho: '9rem' },
            { id: 'a', titulo: 'Activa', celda: (f) => si(f['activa']), ancho: '5rem' },
          ],
          campos: [
            {
              id: 'codigo',
              etiqueta: 'Código',
              requerido: true,
              soloCrear: true,
              patron: /^[A-Z0-9_]{2,10}$/,
            },
            {
              id: 'rut',
              etiqueta: 'RUT (sin DV)',
              requerido: true,
              soloCrear: true,
              patron: /^[0-9]{7,9}$/,
            },
            { id: 'dv', etiqueta: 'DV', requerido: true, soloCrear: true, patron: /^[0-9K]$/ },
            { id: 'razonSocial', etiqueta: 'Razón social', requerido: true },
            { id: 'nombreFantasia', etiqueta: 'Nombre de fantasía' },
            { id: 'giro', etiqueta: 'Giro' },
            { id: 'direccion', etiqueta: 'Dirección' },
            { id: 'comuna', etiqueta: 'Comuna' },
            { id: 'correoContacto', etiqueta: 'Correo de contacto' },
          ],
          valoresDe: (f) => ({
            razonSocial: t(f['razon_social']),
            nombreFantasia: t(f['nombre_fantasia']),
            giro: t(f['giro']),
            direccion: t(f['direccion']),
            comuna: t(f['comuna']),
            correoContacto: t(f['correo_contacto']),
          }),
          crear: (v, motivo) => ({
            ruta: '/empresas',
            cuerpo: {
              codigo: v['codigo'],
              rut: v['rut'],
              dv: (v['dv'] ?? '').toUpperCase(),
              razonSocial: v['razonSocial'],
              ...siTexto('nombreFantasia', v['nombreFantasia']),
              ...siTexto('giro', v['giro']),
              ...siTexto('direccion', v['direccion']),
              ...siTexto('comuna', v['comuna']),
              ...siTexto('correoContacto', v['correoContacto']),
              motivo,
            },
          }),
          editar: (f, v, motivo) => ({
            ruta: `/empresas/${t(f.id)}`,
            metodo: 'PATCH',
            cuerpo: {
              version: Number(f['version']),
              razonSocial: v['razonSocial'],
              nombreFantasia: v['nombreFantasia']?.trim() || null,
              giro: v['giro']?.trim() || null,
              direccion: v['direccion']?.trim() || null,
              comuna: v['comuna']?.trim() || null,
              correoContacto: v['correoContacto']?.trim() || null,
              motivo,
            },
          }),
          conMotivo: true,
          permisoCrear: 'organizacion.empresas.gestionar',
          permisoEditar: 'organizacion.empresas.gestionar',
          acciones: [
            {
              texto: 'Activar / desactivar',
              conMotivo: true,
              permiso: 'organizacion.empresas.gestionar',
              pedido: (f, motivo) => ({
                ruta: `/empresas/${t(f.id)}/activa`,
                cuerpo: { activa: !f['activa'], motivo },
              }),
            },
          ],
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

// ================================================================ parámetros

export function Parametros() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  return (
    <Mantenedor
      def={
        {
          etiqueta: 'Parámetros',
          claveConsulta: ['config', 'parametros', empresaId],
          listar: () => api.get<Fila[]>('/parametros', { query: { empresaId } }),
          clave: (f) => t(f.id),
          textoDe: (f) => `${t(f['clave'])} ${JSON.stringify(f['valor'])}`,
          columnas: [
            {
              id: 'c',
              titulo: 'Clave',
              celda: (f) => <span className="mono">{t(f['clave'])}</span>,
            },
            {
              id: 'v',
              titulo: 'Valor',
              celda: (f) => <span className="mono">{JSON.stringify(f['valor'])}</span>,
            },
            {
              id: 'a',
              titulo: 'Alcance',
              celda: (f) => (f['empresa_id'] ? 'empresa' : 'global'),
              ancho: '7rem',
            },
            {
              id: 'd',
              titulo: 'Desde',
              celda: (f) => fechaHora(t(f['vigencia_desde'])),
              ancho: '10rem',
            },
          ],
          campos: [
            {
              id: 'clave',
              etiqueta: 'Clave',
              requerido: true,
              patron: /^[a-z0-9_.]{3,80}$/,
              ayuda: 'minúsculas, números, punto y guion bajo',
            },
            { id: 'valor', etiqueta: 'Valor (JSON o texto)', tipo: 'area', requerido: true },
            { id: 'global', etiqueta: 'Global (todas las empresas)', tipo: 'booleano' },
          ],
          crear: (v, motivo) => {
            let valor: unknown = v['valor'] ?? '';
            try {
              valor = JSON.parse(String(v['valor']));
            } catch {
              /* texto plano */
            }
            return {
              ruta: '/parametros',
              cuerpo: {
                clave: v['clave'],
                valor,
                ...(v['global'] === 'true' ? {} : { empresaId }),
                motivo,
              },
            };
          },
          conMotivo: true,
          permisoCrear: 'organizacion.parametros.editar',
          nota: (
            <p className="apagado">
              Un parámetro nuevo con la misma clave reemplaza al anterior desde hoy (queda la
              historia).
            </p>
          ),
        } satisfies DefMantenedor<Fila>
      }
    />
  );
}

// ================================================================ API keys

export function ApiKeys() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [clave, setClave] = useState<string | null>(null);
  return (
    <>
      {clave && (
        <div className="sin-fuente" role="alert">
          <b>Copia la clave AHORA: no se vuelve a mostrar.</b>
          <span className="mono">{clave}</span>
          <button type="button" onClick={() => setClave(null)}>
            Ya la guardé
          </button>
        </div>
      )}
      <Mantenedor
        def={
          {
            etiqueta: 'API keys',
            claveConsulta: ['config', 'api-keys'],
            listar: () => api.get<Fila[]>('/api-keys'),
            clave: (f) => t(f.id),
            textoDe: (f) => `${t(f['nombre'])} ${t(f['prefijo'])}`,
            columnas: [
              { id: 'n', titulo: 'Nombre', celda: (f) => t(f['nombre']) },
              {
                id: 'p',
                titulo: 'Prefijo',
                celda: (f) => <span className="mono">{t(f['prefijo'])}</span>,
                ancho: '9rem',
              },
              { id: 'e', titulo: 'Expira', celda: (f) => fecha(t(f['expira_en'])), ancho: '8rem' },
              {
                id: 'u',
                titulo: 'Último uso',
                celda: (f) => fechaHora(t(f['ultimo_uso_en'])),
                ancho: '10rem',
              },
              {
                id: 'r',
                titulo: 'Estado',
                celda: (f) => (f['revocada_en'] ? 'revocada' : 'activa'),
                ancho: '7rem',
              },
            ],
            campos: [
              { id: 'nombre', etiqueta: 'Nombre', requerido: true },
              {
                id: 'permisos',
                etiqueta: 'Permisos (api.…, separados por coma)',
                requerido: true,
                patron: /^api\.[a-z0-9_.]+(\s*,\s*api\.[a-z0-9_.]+)*$/,
              },
              { id: 'diasVigencia', etiqueta: 'Días de vigencia', tipo: 'numero' },
            ],
            crear: (v, motivo) => ({
              ruta: '/api-keys',
              cuerpo: {
                nombre: v['nombre'],
                empresaId,
                permisos: String(v['permisos'])
                  .split(',')
                  .map((x) => x.trim()),
                ...(v['diasVigencia'] ? { diasVigencia: Number(v['diasVigencia']) } : {}),
                motivo,
              },
            }),
            conMotivo: true,
            permisoCrear: 'organizacion.api_keys.gestionar',
            alCreado: (r) => {
              const c = (r as { unaVez?: { clave?: string } } | null)?.unaVez?.clave;
              if (c) setClave(c);
            },
            acciones: [
              {
                texto: 'Revocar',
                conMotivo: true,
                permiso: 'organizacion.api_keys.gestionar',
                visible: (f) => !f['revocada_en'],
                pedido: (f, motivo) => ({
                  ruta: `/api-keys/${t(f.id)}/revocar`,
                  cuerpo: { motivo },
                }),
              },
            ],
          } satisfies DefMantenedor<Fila>
        }
      />
    </>
  );
}

// ================================================================ auditoría

export function Auditoria() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [sensibles, setSensibles] = useState(false);
  const [agregado, setAgregado] = useState('');
  const q = useQuery({
    queryKey: ['config', 'auditoria', empresaId, sensibles, agregado],
    queryFn: () =>
      api.get<Fila[]>('/auditoria', {
        query: {
          empresaId,
          limite: 300,
          ...(sensibles ? { soloSensibles: 'true' } : {}),
          ...(agregado ? { agregado } : {}),
        },
      }),
  });
  return (
    <div className="pantalla consulta" data-pantalla="auditoria">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Agregado</span>
          <input
            aria-label="Agregado"
            placeholder="ventas.nota_venta, compras.orden…"
            value={agregado}
            onChange={(e) => setAgregado(e.target.value.trim())}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={sensibles}
            onChange={(e) => setSensibles(e.target.checked)}
          />{' '}
          Solo sensibles
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Auditoría">
        <thead>
          <tr>
            <th style={{ width: '10rem' }}>Cuándo</th>
            <th>Comando</th>
            <th>Agregado</th>
            <th>Resultado</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((f) => (
            <tr key={t(f.id)} className={f['sensible'] ? 'tono-aviso' : undefined}>
              <td>{fechaHora(t(f['ocurrido_en']))}</td>
              <td className="mono">{t(f['comando'])}</td>
              <td className="mono">{t(f['agregado'])}</td>
              <td>{t(f['resultado'])}</td>
              <td>{t(f['motivo'])}</td>
            </tr>
          ))}
          {!q.data?.length && (
            <tr>
              <td colSpan={5} className="grilla-vacia">
                {q.isLoading ? 'Cargando…' : 'Sin registros'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
